import * as Array from "fp-ts/Array";
import {
  __,
  addIndex,
  applySpec,
  chain,
  difference,
  divide,
  fromPairs,
  identity,
  isEmpty,
  isNil,
  map,
  multiply,
  not,
  pluck,
  prop,
  propEq,
  reject,
  sum,
  toPairs,
  type,
  complement,
  either,
} from "ramda";
import * as Option from "fp-ts/lib/Option";
import { v5 as uuidV5 } from "uuid";
import { flow, pipe } from "fp-ts/lib/function";
import currency from "currency.js";
import { EjiItem, EjiService, Job } from "../generated/nest-graphql";
import { Lens } from "monocle-ts";
import { floridaMarkets } from "./constants";

export const objectDiff = (newObj, oldObj) => {
  return fromPairs<any>(difference(toPairs(newObj), toPairs(oldObj)) as any);
};

export const flattenObj = (obj) => {
  const go = (obj_) =>
    chain(([k, v]: any[]) => {
      if (type(v) === "Object") {
        return map(([k_, v_]) => [`${k}.${k_}`, v_], go(v));
      } else {
        return [[k, v]];
      }
    }, toPairs(obj_) as any);
  return fromPairs(go(obj));
};
const unflatten = (obj) => {
  const result = {};
  for (const objectKey in obj) {
    const keys = objectKey.split(".");
    keys.reduce(function (r, e, j) {
      return r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 === j ? obj[objectKey] : {}) : []);
    }, result);
  }
  return result;
};

export const cleanObject = (obj) => {
  return flow(flattenObj, reject(isNil), unflatten)(obj);
};

export const cleanEmptyObject = (obj) => {
  return flow(flattenObj, reject(isNil), reject(isEmpty), unflatten)(obj);
};

export const calculateSubtotalFromValues = (values: any) =>
  flow(prop("items"), pluck("amount") as any, map(toNumber), map(multiply(100)), sum, divide(__, 100))(values);

export const handleNoDecimal = (val: string) => {
  return val.includes(".") ? val : val + ".00";
};

export const toReactSelectOptions = (options: string[]) =>
  map(
    applySpec({
      value: identity,
      label: identity,
    })
  )(options);

export const toNumber = (val: any) => {
  return Number(val);
};

export const mapIndexed = addIndex(map);

export const stringToDate = (val: string) =>
  flow(
    Option.fromNullable,
    Option.map((innerVal: string) => {
      return new Date(innerVal);
    }),
    Option.toNullable
  )(val);

export const isStripeReady = (values: any) => {
  return !!prop("stripeCustomer", values) && !isEmpty(prop("stripePaymentMethods", values));
};

export const hashIdempotencyKey = async (content) => {
  return uuidV5(content, uuidV5.DNS);
};

export const calculateTotalByTypes = (type) => (ejiServices: EjiService[]) =>
  pipe(
    ejiServices,
    Array.map(Lens.fromProp<EjiService>()("items").get),
    Array.flatten,
    Array.filter(propEq("category", type)),
    Array.map(Lens.fromProp<EjiItem>()("customerPrice").get),
    Array.filter((val) => not(isNil(val))),
    Array.map(toNumber),
    sum,
    currency
  );

export const calculateLaborTotalFromServices = calculateTotalByTypes("Labor");
export const calculatePartsTotalFromServices = calculateTotalByTypes("Part");
export const calculateFeeTotalFromServices = calculateTotalByTypes("Fee");

export const tryJSONStringify = (obj: any) => {
  try {
    // JSON.stringify throws if there are cycles.
    return JSON.stringify(obj);
  } catch (_) {
    return obj;
  }
};

export const exists = (a: any): boolean => complement(either(isNil, isEmpty))(a);

export const isFlorida = (job: Job) =>
  floridaMarkets.includes(job?.market) || job?.serviceLocationAddress?.state === "FL";
